'use client';
import Link from 'next/link';
import Image from 'next/image';
import { usePathname } from 'next/navigation';
import { useTranslations as useTranslations$1 } from 'next-intl';
import { DOCS_HOME } from '@/lib/docs';
import { DocsChat } from '@/components/docs/DocsChat';
import { LearnSearch } from './LearnSearch';
import { LearnAccessCta } from './LearnAccessCta';
/**
 * The Learn surface's own top bar — logo + Docs/Tutorials/Skills tabs + centered
 * search — matching the public Learn site (martini-learn). Rendered by DocsShell
 * instead of the shared AppHeader, so the Learn area reads like standalone docs.
 */ export function LearnHeader() {
    const t = useTranslations$1();
    const pathname = usePathname() ?? '';
    // Tutorials covers the video lessons + community; doc articles (`/docs/*`),
    // glossary, and shortcuts live under the Docs tab; skills render under
    // /skills. The `/learn` landing is a neutral overview, so no content tab is
    // selected there.
    const isLanding = pathname === '/learn';
    const isTutorials = pathname.startsWith('/learn/tutorials') || pathname === '/learn/community';
    const isSkills = pathname.startsWith('/skills');
    // `/learn` is the overview home (entered from any in-app Learn link). The Learn
    // tab returns to it; Docs, Tutorials, and Skills lead into their content. Docs
    // points at the Welcome article (not back to the landing) so it actually enters
    // Docs and marks the tab selected. Skills links to its one entry so far.
    const tabs = [
        {
            label: t("IbrSk1"),
            href: '/learn',
            active: isLanding
        },
        {
            label: t("_6_sPX"),
            href: DOCS_HOME,
            active: !isLanding && !isTutorials && !isSkills
        },
        {
            label: t("4u7hBB"),
            href: '/learn/tutorials',
            active: isTutorials
        },
        {
            label: t("EJSVsO"),
            href: '/skills/blender',
            active: isSkills
        }
    ];
    return (<header className="sticky top-0 z-50 border-b border-border bg-background/80 backdrop-blur-xl">
      <div className="mx-auto flex h-12 max-w-[1360px] items-center px-6">
        { /* Left: logo + tabs. Tabs scroll rather than push the search and the
            account CTA off a phone-width header. */ }
        <div className="flex min-w-0 items-center gap-1">
          { /* The marketing home, not /projects: Learn is public and /projects is
              noindexed + robots-disallowed, so the surface's most prominent link
              should pass somewhere. Signed-in users get Go to Projects beside it. */ }
          <Link href="/" aria-label={t("DBaNQ7")} className="me-2 flex items-center gap-2.5">
            { /* eslint-disable-next-line i18next/no-literal-string -- Martini is a brand name */ }
            <Image src="/whitemartini.svg" alt="Martini" width={18} height={17}/>
          </Link>
          <nav className="no-scrollbar flex items-center gap-0.5 overflow-x-auto">
            {tabs.map((tab)=>(<Link key={tab.href} href={tab.href} className={`rounded-md px-3 py-1.5 text-[13px] font-medium transition-colors ${tab.active ? 'bg-white/10 text-foreground' : 'text-muted-foreground hover:bg-white/5 hover:text-foreground'}`}>
                {tab.label}
              </Link>))}
          </nav>
        </div>

        { /* Center: search */ }
        <div className="flex min-w-0 flex-1 justify-center px-2">
          <LearnSearch/>
        </div>

        { /* Right: Discord + Ask AI (rendered by DocsChat, which owns the drawer)
            and the account CTA. In flow rather than a fixed corner cluster so
            the CTA can never be overlapped by the pills. */ }
        <div className="flex shrink-0 items-center gap-2">
          <DocsChat/>
          <LearnAccessCta/>
        </div>
      </div>
    </header>);
}
